import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Upload, LogOut, Image as ImgIcon, Mail, X, Check, GripVertical, FileText, BarChart3 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEFAULT_TEXTS, TEXT_LABELS } from "../texts";
import { PLATFORMS, getPlatformMeta } from "../socials";
import { refreshTexts } from "../hooks/useTexts";
import RichEditor from "../components/RichEditor";
import AnalyticsPanel from "../components/AnalyticsPanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login", { replace: true });
    else if (!user.is_admin) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user || !user.is_admin) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#4d6a3a] font-cursive text-3xl animate-pulse">Cargando…</div>;
  }

  const TABS = [
    { k: "upcoming", l: "Próximos eventos" },
    { k: "past", l: "Eventos pasados" },
    { k: "messages", l: "Mensajes" },
    { k: "texts", l: "Textos" },
    { k: "socials", l: "Redes" },
    { k: "analytics", l: "Estadísticas" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-cursive text-5xl text-[#4d6a3a]">Panel Admin</h1>
          <p className="text-[#5c6b47]">Hola, {user.name} · {user.email}</p>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#cddab6] hover:bg-[#f4f7ec]">
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </div>

      <div className="mt-8 flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.k ? "bg-[#7a8e5a] text-white" : "bg-white border border-[#cddab6] text-[#3d4a30] hover:bg-[#f4f7ec]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "messages" && <MessagesPanel />}
        {tab === "texts" && <TextsPanel />}
        {tab === "socials" && <SocialsPanel />}
        {tab === "analytics" && <AnalyticsPanel />}
        {(tab === "upcoming" || tab === "past") && <EventsPanel upcoming={tab === "upcoming"} />}
      </div>
    </div>
  );
}

/* ---------------- Events ---------------- */
function EventsPanel({ upcoming }) {
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/events`, { params: { upcoming } });
    setEvents(data);
  }, [upcoming]);
  useEffect(() => { load(); }, [load]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = events.findIndex((x) => x.id === active.id);
    const newIndex = events.findIndex((x) => x.id === over.id);
    const reordered = arrayMove(events, oldIndex, newIndex);
    setEvents(reordered);
    setSavingOrder(true);
    try {
      await axios.post(`${API}/events/reorder`, { ids: reordered.map((r) => r.id) }, { withCredentials: true });
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-cursive text-3xl text-[#4d6a3a]">{upcoming ? "Próximos eventos" : "Eventos pasados"}</h2>
        <div className="flex items-center gap-3">
          {savingOrder && <span className="text-xs text-[#5c6b47]">Guardando orden…</span>}
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7a8e5a] text-white hover:bg-[#6b7d51]">
            <Plus className="w-4 h-4" /> Nuevo evento
          </button>
        </div>
      </div>

      <p className="text-xs text-[#5c6b47] italic mb-3">💡 Arrastra el icono <GripVertical className="inline w-3 h-3" /> para reordenar los eventos.</p>

      {creating && (
        <EventForm
          initial={{ title: "", date: "", location: "", description: "", image_url: "", is_upcoming: upcoming, order: 0 }}
          onCancel={() => setCreating(false)}
          onSave={async (payload) => {
            await axios.post(`${API}/events`, payload, { withCredentials: true });
            setCreating(false);
            load();
          }}
        />
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={events.map((e) => e.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4 mt-4">
            {events.map((ev) => (
              <SortableEventRow key={ev.id} ev={ev} reload={load} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {events.length === 0 && <p className="text-[#5c6b47] italic">Sin eventos todavía.</p>}
    </div>
  );
}

function SortableEventRow({ ev, reload }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ev.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <EventRow ev={ev} reload={reload} dragHandle={{ ...attributes, ...listeners }} />
    </div>
  );
}

function EventForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/cover-new`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_url", data.image_url);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-[#cddab6] rounded-2xl p-6 shadow">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Título"><input value={form.title} onChange={(e) => set("title", e.target.value)} className="inp" /></Field>
        <Field label="Fecha (DD/MM/YYYY)"><input value={form.date} onChange={(e) => set("date", e.target.value)} className="inp" /></Field>
        <Field label="Ubicación"><input value={form.location} onChange={(e) => set("location", e.target.value)} className="inp" /></Field>
        <Field label="Orden"><input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || 0))} className="inp" /></Field>
        <Field label="Estado">
          <select value={form.is_upcoming ? "1" : "0"} onChange={(e) => set("is_upcoming", e.target.value === "1")} className="inp">
            <option value="1">Próximo</option>
            <option value="0">Pasado</option>
          </select>
        </Field>
        <Field label="Foto de portada">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white border border-[#cddab6] hover:bg-[#f4f7ec] cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : "Subir imagen"}
              <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} />
            </label>
            {form.image_url && (
              <img src={absUrl(form.image_url)} alt="" className="w-16 h-12 object-cover rounded" />
            )}
          </div>
          <input
            value={form.image_url}
            onChange={(e) => set("image_url", e.target.value)}
            placeholder="o pega una URL"
            className="inp mt-2 text-xs"
          />
        </Field>
      </div>
      <Field label="Descripción"><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="inp resize-none" /></Field>
      <div className="flex gap-2 mt-4">
        <button onClick={() => onSave(form)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7a8e5a] text-white hover:bg-[#6b7d51]"><Save className="w-4 h-4" /> Guardar</button>
        <button onClick={onCancel} className="px-4 py-2 rounded-full bg-white border border-[#cddab6] hover:bg-[#f4f7ec]">Cancelar</button>
      </div>
      <style>{`.inp{width:100%;padding:.5rem .75rem;border:1px solid #cddab6;border-radius:.5rem;outline:none;background:#fff;} .inp:focus{box-shadow:0 0 0 3px rgba(122,142,90,.25);}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block font-semibold text-[#3d4a30] mb-1">{label}</span>
      {children}
    </label>
  );
}

function EventRow({ ev, reload, dragHandle }) {
  const [editing, setEditing] = useState(false);
  const [gallery, setGallery] = useState(ev.gallery || []);

  useEffect(() => { setGallery(ev.gallery || []); }, [ev.gallery]);

  const uploadPhotos = async (files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    const { data } = await axios.post(`${API}/events/${ev.id}/photos`, fd, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    });
    setGallery(data.gallery);
  };

  const delPhoto = async (u) => {
    const { data } = await axios.delete(`${API}/events/${ev.id}/photos`, { params: { url: u }, withCredentials: true });
    setGallery(data.gallery);
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar evento?")) return;
    await axios.delete(`${API}/events/${ev.id}`, { withCredentials: true });
    reload();
  };

  return (
    <div className="bg-white border border-[#e6ecd7] rounded-2xl p-5 shadow-sm">
      {editing ? (
        <EventForm
          initial={{ ...ev }}
          onCancel={() => setEditing(false)}
          onSave={async (payload) => {
            await axios.put(`${API}/events/${ev.id}`, payload, { withCredentials: true });
            setEditing(false);
            reload();
          }}
        />
      ) : (
        <div className="flex gap-4 items-start">
          <button {...dragHandle} className="p-2 cursor-grab active:cursor-grabbing text-[#a5b58e] hover:text-[#4d6a3a] shrink-0" title="Arrastrar para reordenar">
            <GripVertical className="w-5 h-5" />
          </button>
          <div className="w-32 h-24 rounded-lg overflow-hidden bg-[#f4f7ec] shrink-0">
            {ev.image_url ? <img src={absUrl(ev.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full"><ImgIcon className="w-6 h-6 text-[#a5b58e]" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#5c6b47]">{ev.date} {ev.location ? `· ${ev.location}` : ""}</div>
            <h3 className="font-cursive text-2xl text-[#4d6a3a] leading-tight">{ev.title}</h3>
            <p className="text-sm text-[#3d4a30] mt-1 line-clamp-2">{ev.description}</p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-full text-xs bg-white border border-[#cddab6] hover:bg-[#f4f7ec]">Editar</button>
            <button onClick={remove} className="px-3 py-1.5 rounded-full text-xs bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Borrar</button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#e6ecd7]">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-[#3d4a30]">Galería ({gallery.length})</h4>
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-[#7a8e5a] text-white cursor-pointer hover:bg-[#6b7d51]">
            <Upload className="w-3 h-3" /> Subir fotos
            <input type="file" accept="image/*" multiple hidden onChange={(e) => uploadPhotos(e.target.files)} />
          </label>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3">
          {gallery.map((u) => (
            <div key={u} className="relative group aspect-square rounded overflow-hidden">
              <img src={absUrl(u)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => delPhoto(u)} className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition">
                <X className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Messages ---------------- */
function MessagesPanel() {
  const [msgs, setMsgs] = useState([]);
  const load = async () => {
    const { data } = await axios.get(`${API}/messages`, { withCredentials: true });
    setMsgs(data);
  };
  useEffect(() => { load(); }, []);
  const markRead = async (m) => {
    await axios.patch(`${API}/messages/${m.id}`, {}, { params: { is_read: !m.is_read }, withCredentials: true });
    load();
  };
  const del = async (m) => {
    if (!window.confirm("¿Eliminar mensaje?")) return;
    await axios.delete(`${API}/messages/${m.id}`, { withCredentials: true });
    load();
  };

  return (
    <div>
      <h2 className="font-cursive text-3xl text-[#4d6a3a] mb-4 inline-flex items-center gap-2"><Mail className="w-6 h-6" /> Mensajes recibidos</h2>
      <div className="space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className={`p-5 rounded-2xl border ${m.is_read ? "bg-white border-[#e6ecd7]" : "bg-[#f4f7ec] border-[#a5b58e]"}`}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="font-semibold text-[#3d4a30]">{m.name}</h3>
              <a href={`mailto:${m.email}`} className="text-sm text-[#4d6a3a] link-underline">{m.email}</a>
              <span className="text-xs text-[#5c6b47] ml-auto">{new Date(m.created_at).toLocaleString("es-ES")}</span>
            </div>
            {m.subject && <div className="text-sm mt-1 font-semibold text-[#4d6a3a]">{m.subject}</div>}
            <p className="text-sm text-[#3d4a30] mt-2 whitespace-pre-wrap">{m.message}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => markRead(m)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-white border border-[#cddab6] hover:bg-[#f4f7ec]">
                <Check className="w-3 h-3" /> {m.is_read ? "Marcar no leído" : "Marcar leído"}
              </button>
              <button onClick={() => del(m)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">
                <Trash2 className="w-3 h-3" /> Borrar
              </button>
            </div>
          </div>
        ))}
        {msgs.length === 0 && <p className="text-[#5c6b47] italic">Aún no hay mensajes.</p>}
      </div>
    </div>
  );
}

/* ---------------- Texts ---------------- */
function TextsPanel() {
  const [texts, setTexts] = useState({ ...DEFAULT_TEXTS });
  const [savedKey, setSavedKey] = useState(null);
  const timers = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/texts`);
        setTexts({ ...DEFAULT_TEXTS, ...data });
      } catch (e) {
        console.warn("Admin: no se pudieron cargar los textos", e);
      }
    })();
  }, []);

  const save = async (key, value) => {
    await axios.put(`${API}/texts/${key}`, { value }, { withCredentials: true });
    setSavedKey(key);
    setTimeout(() => setSavedKey(null), 1500);
    refreshTexts();
  };

  const resetOne = async (key) => {
    if (!window.confirm("¿Restablecer al texto por defecto?")) return;
    await save(key, DEFAULT_TEXTS[key]);
    setTexts((t) => ({ ...t, [key]: DEFAULT_TEXTS[key] }));
  };

  const change = (key, value) => {
    setTexts((t) => ({ ...t, [key]: value }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => save(key, value), 900);
  };

  return (
    <div>
      <h2 className="font-cursive text-3xl text-[#4d6a3a] mb-2 inline-flex items-center gap-2"><FileText className="w-6 h-6" /> Textos del sitio</h2>
      <p className="text-sm text-[#5c6b47] mb-6">Los cambios se guardan automáticamente al dejar de escribir.</p>
      <div className="space-y-6">
        {Object.entries(TEXT_LABELS).map(([key, label]) => {
          const isPlainLine = key.includes("title") || key === "hero_badge" || key === "hero_title_1" || key === "hero_title_2";
          const isPlainText = key === "hero_subtitle";
          const val = texts[key] ?? "";
          return (
            <div key={key} className="bg-white border border-[#e6ecd7] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="font-semibold text-[#3d4a30]">{label}</label>
                <div className="flex items-center gap-3">
                  {savedKey === key && <span className="text-xs text-green-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
                  <button onClick={() => resetOne(key)} className="text-xs text-[#5c6b47] link-underline">Restablecer</button>
                </div>
              </div>
              {isPlainLine ? (
                <input value={val} onChange={(e) => change(key, e.target.value)} className="w-full px-3 py-2 border border-[#cddab6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7a8e5a]/40" />
              ) : isPlainText ? (
                <textarea rows={3} value={val} onChange={(e) => change(key, e.target.value)} className="w-full px-3 py-2 border border-[#cddab6] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#7a8e5a]/40" />
              ) : (
                <RichEditor value={val} onChange={(v) => change(key, v)} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ---------------- Socials ---------------- */
function SocialsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newPlatform, setNewPlatform] = useState("instagram");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/socials`);
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addItem = () => {
    const meta = getPlatformMeta(newPlatform);
    setItems((arr) => [
      ...arr,
      {
        id: `${Date.now()}`,
        platform: newPlatform,
        label: meta.label,
        url: "",
        enabled: true,
        order: arr.length,
      },
    ]);
  };

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx) => {
    setItems((arr) => arr.filter((_, i) => i !== idx));
  };

  const moveItem = (idx, dir) => {
    setItems((arr) => {
      const nx = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= nx.length) return arr;
      [nx[idx], nx[j]] = [nx[j], nx[idx]];
      return nx.map((it, i) => ({ ...it, order: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = items
        .filter((it) => it.url)
        .map((it, i) => ({ ...it, order: i }));
      await axios.put(`${API}/socials`, payload, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      // Notify footer to reload
      window.dispatchEvent(new CustomEvent("pf:socials:refresh"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[#5c6b47] italic">Cargando…</div>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-cursive text-3xl text-[#4d6a3a]">Redes sociales</h2>
          <p className="text-sm text-[#5c6b47]">Estas redes se muestran en el pie de página.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7a8e5a] text-white hover:bg-[#6b7d51] disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Publicar cambios"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#cddab6] rounded-2xl p-5 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-semibold text-[#3d4a30] mb-1">Añadir red</label>
            <select
              value={newPlatform}
              onChange={(e) => setNewPlatform(e.target.value)}
              className="w-full px-3 py-2 border border-[#cddab6] rounded-lg outline-none focus:ring-2 focus:ring-[#7a8e5a]/40 bg-white"
            >
              {Object.entries(PLATFORMS).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#cddab6] hover:bg-[#f4f7ec]"
          >
            <Plus className="w-4 h-4" /> Añadir
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((it, idx) => {
          const meta = getPlatformMeta(it.platform);
          const Icon = meta.icon;
          return (
            <div key={it.id || idx} className="bg-white border border-[#e6ecd7] rounded-2xl p-4 flex flex-wrap items-center gap-3">
              <span className="p-2 rounded-full" style={{ background: `${meta.color}15`, color: meta.color }}>
                <Icon className="w-5 h-5" />
              </span>
              <select
                value={it.platform}
                onChange={(e) => updateItem(idx, { platform: e.target.value, label: getPlatformMeta(e.target.value).label })}
                className="px-3 py-2 border border-[#cddab6] rounded-lg outline-none bg-white text-sm"
              >
                {Object.entries(PLATFORMS).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
              <input
                value={it.url}
                onChange={(e) => updateItem(idx, { url: e.target.value })}
                placeholder={meta.placeholder}
                className="flex-1 min-w-[240px] px-3 py-2 border border-[#cddab6] rounded-lg outline-none focus:ring-2 focus:ring-[#7a8e5a]/40 text-sm"
              />
              {it.platform === "other" && (
                <input
                  value={it.label || ""}
                  onChange={(e) => updateItem(idx, { label: e.target.value })}
                  placeholder="Nombre"
                  className="w-32 px-3 py-2 border border-[#cddab6] rounded-lg outline-none text-sm"
                />
              )}
              <label className="inline-flex items-center gap-2 text-xs text-[#3d4a30]">
                <input
                  type="checkbox"
                  checked={it.enabled}
                  onChange={(e) => updateItem(idx, { enabled: e.target.checked })}
                />
                Visible
              </label>
              <div className="flex gap-1">
                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="px-2 py-1 rounded border border-[#cddab6] hover:bg-[#f4f7ec] disabled:opacity-40 text-xs">↑</button>
                <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="px-2 py-1 rounded border border-[#cddab6] hover:bg-[#f4f7ec] disabled:opacity-40 text-xs">↓</button>
              </div>
              <button
                onClick={() => removeItem(idx)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
              >
                <Trash2 className="w-3 h-3" /> Quitar
              </button>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-[#5c6b47] italic">Aún no hay redes. Añade la primera usando el selector de arriba.</p>
        )}
      </div>
    </div>
  );
}
